import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { animate, style, transition, trigger } from '@angular/animations';
import { Observable, Subject, Subscription, fromEvent, of } from 'rxjs';
import { debounceTime, delay, map, takeUntil, tap } from 'rxjs/operators';
import {QuestionTagsSelectorService} from './question-tags-selector.service';
import {ITag, ITagSearchResponse} from './question-tags-selector.interface';

@Component({
  selector: 'curr-question-tags-selector',
  templateUrl: './question-tags-selector.component.html',
  styleUrls: ['./question-tags-selector.component.scss'],
  providers: [QuestionTagsSelectorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-4px)' }),
        animate('120ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-4px)' })),
      ]),
    ]),
  ],
})
export class QuestionTagsSelectorComponent implements OnInit, OnDestroy {
  @Input() curriculumId!: number;
  @Input() isEditMode = true;
  @Input() set savedTags(value: ITag[] | undefined) {
    this._savedTags = Array.isArray(value) ? value : [];
    this.applySavedTags();
  }
  @Output() selectedTagsChange = new EventEmitter<ITag[]>();

  // View
  @ViewChild('tagInput', { static: true }) tagInput!: ElementRef<HTMLInputElement>;
  @ViewChild('panelEl') panelEl?: ElementRef<HTMLElement>;

  // Reactive control
  readonly inputCtrl = new FormControl('');

  // State
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedTags: ITag[] = [];

  // Pagination
  pageSize = 10;
  currentPage = 0;
  hasMore = true;
  loading = false;

  // Rendered options for current filter
  visibleOptions: ITag[] = [];

  isPanelOpen = false;

  private _allTags: ITag[] = [];
  private _savedTags: ITag[] = [];
  private destroy$ = new Subject<void>();
  private panelScrollSub?: Subscription;

  constructor(private cdr: ChangeDetectorRef, private questionTagsSelectorService: QuestionTagsSelectorService) {}

  ngOnInit(): void {
    this.questionTagsSelectorService.getQuestionTags(this.curriculumId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: ITagSearchResponse) => {
        this._allTags = (resp && Array.isArray(resp.results)) ? resp.results.slice() : [];
        this.applySavedTags();
    });

    // React to input text changes
    this.inputCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        tap(() => {
          this.currentPage = 0;
          this.hasMore = true;
          this.visibleOptions = [];
          this.loading = false;
        }),
        tap(() => this.loadNextPage())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByValue = (_: number, v: ITag) => v?.id ?? v?.tag;

  isSelected(tag: ITag): boolean {
    return this.selectedTags.findIndex(t => (t.id ? t.id === tag.id : t.tag.toLowerCase() === tag.tag.toLowerCase())) > -1;
  }

  remove(tag: ITag): void {
    this.toggleTagSelection(tag);
  }

  toggleTagSelection(tag: ITag): void {
    const idx = this.selectedTags.findIndex(t => (t.id ? t.id === tag.id : t.tag.toLowerCase() === tag.tag.toLowerCase()));
    if (idx > -1) {
      this.selectedTags.splice(idx, 1);
    } else {
      this.selectedTags.push(tag);
    }
    this.emitChange();
    this.cdr.markForCheck();
  }

  addNewFromInput(): void {
    const raw = (this.inputCtrl.value || '').toString().trim();
    if (!raw) { return; }

    const exists = this._allTags.find(t => t.tag.toLowerCase() === raw.toLowerCase());
    const tag: ITag = exists ?? { id: 0 as unknown as number, tag: raw };
    if (!exists) {
      this._allTags.unshift(tag);
    }
    if (!this.isSelected(tag)) {
      this.selectedTags.push(tag);
      this.emitChange();
    }

    const filterText = (this.inputCtrl.value || '').toString().toLowerCase();
    if (tag.tag.toLowerCase().includes(filterText) && this.visibleOptions.findIndex(t => (t.id ? t.id === tag.id : t.tag.toLowerCase() === tag.tag.toLowerCase())) === -1) {
      this.visibleOptions = [tag, ...this.visibleOptions];
    }

    this.clearInputKeepPanel();
    this.currentPage = 0;
    this.hasMore = true;
    this.visibleOptions = [];
    this.loading = false;
    this.cdr.markForCheck();
    this.loadNextPage();
  }

  onBackspace(ev: KeyboardEvent): void {
    if (!this.inputCtrl.value && this.selectedTags.length) {
      this.remove(this.selectedTags[this.selectedTags.length - 1]);
    }
  }

  togglePanel(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isPanelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel(): void {
    if (!this.isPanelOpen) {
      this.onOpened();
    }
  }

  closePanel(): void {
    if (this.isPanelOpen) {
      this.onClosed();
    }
  }

  onOptionClicked(tag: ITag): void {
    this.toggleTagSelection(tag);

    if (this.tagInput?.nativeElement && this.tagInput.nativeElement.value !== '') {
      this.clearInputKeepPanel();
      this.currentPage = 0;
      this.hasMore = true;
      this.visibleOptions = [];
      this.loading = false;
      this.cdr.markForCheck();
      this.loadNextPage();
    } else {
      this.clearInputKeepPanel();
    }

    // this.resetFilterAndLoad();
    this.cdr.markForCheck();
  }

  onOpened(): void {
    this.isPanelOpen = true;
    this.attachScrollListener();

    if (this.visibleOptions.length === 0 && !this.loading) {
      this.currentPage = 0;
      this.hasMore = true;
      this.loadNextPage();
    }
  }

  onClosed(): void {
    this.isPanelOpen = false;
    if (this.panelScrollSub) {
      this.panelScrollSub.unsubscribe();
      this.panelScrollSub = undefined;
    }
  }

  private attachScrollListener(): void {
    setTimeout(() => {
      const panelEl: HTMLElement | null = this.panelEl?.nativeElement ?? null;
      if (!panelEl) { return; }
      if (this.panelScrollSub) {
        this.panelScrollSub.unsubscribe();
        this.panelScrollSub = undefined;
      }
      this.panelScrollSub = fromEvent(panelEl, 'scroll')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.loading || !this.hasMore) { return; }
          const threshold = 48;
          const atBottom = panelEl.scrollTop + panelEl.clientHeight >= panelEl.scrollHeight - threshold;
          if (atBottom) {
            this.loadNextPage();
          }
        });
    });
  }

  private loadNextPage(): void {
    if (!this.hasMore || this.loading) {
      return;
    }
    this.loading = true;
    const filterText = (this.inputCtrl.value || '').toString().trim();
    const page = this.currentPage;
    const size = this.pageSize;

    this.questionTagsSelectorService
      .getQuestionTags(this.curriculumId, filterText, page, size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: ITagSearchResponse) => {
          const items = Array.isArray(resp?.results) ? resp.results : [];
          this.visibleOptions = [...this.visibleOptions, ...items];

          let hasMore = true;
          if (
            resp &&
            typeof resp.totalResults === 'number' &&
            typeof resp.pageIndex === 'number' &&
            typeof resp.pageSize === 'number'
          ) {
            hasMore = (resp.pageIndex + 1) * resp.pageSize < resp.totalResults;
          } else {
            hasMore = items.length === size;
          }

          this.hasMore = hasMore;
          this.currentPage += 1;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private emitChange(): void {
    this.selectedTagsChange.emit([...this.selectedTags]);
  }

  private clearInputKeepPanel(): void {
    this.inputCtrl.setValue('', { emitEvent: false });

    if (this.tagInput?.nativeElement) {
      this.tagInput.nativeElement.value = '';
    }
  }

  private applySavedTags(): void {
    if (!Array.isArray(this._savedTags)) {
      return;
    }
    const unique: ITag[] = [];

    for (const t of this._savedTags) {
      if (!t || typeof t.tag !== 'string') { continue; }
      const canonical = this._allTags.find(a => (t.id ? a.id === t.id : a.tag?.toLowerCase() === t.tag.toLowerCase())) ?? t;
      const existsIdx = unique.findIndex(u => (u.id ? u.id === canonical.id : u.tag.toLowerCase() === canonical.tag.toLowerCase()));
      if (existsIdx === -1) {
        unique.push(canonical);
      }
    }
    this.selectedTags = unique;
    this.cdr.markForCheck();
  }
}
